// ABOUTME: Root component: routes to the signed-in shell, guest mode, the splash page or a secondary page.
// ABOUTME: Applies the theme, migrates guest data on sign-in, and owns the sign-in and sign-up modals.
import React, { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { inviteService } from "./services/inviteService";
import { normalizeInviteCode } from "./utils/invites";
import { useAuth } from "./hooks/useAuth";
import { useUIStore } from "./stores/ui";
import { guestDashboardService } from "./services/guestDashboardService";
import { guestMigrationService, describeMigration } from "./services/guestMigrationService";
import { applyTheme, ACCENT_PALETTES } from "./utils/adaptiveColor";
import SignInModal from "./components/SignInModal";
import SignUpModal from "./components/SignUpModal";
import SplashPage from "./components/SplashPage";
import GuestApp from "./components/GuestApp";
import AuthenticatedApp from "./components/AuthenticatedApp";
// Secondary routes are split out of the main bundle — most visitors land on the
// dashboard and never touch the admin panel, brand guide, or legal pages.
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const EmailSignIn = lazy(() => import("./pages/EmailSignIn"));
const BrandGuide = lazy(() => import("./pages/BrandGuide"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-[var(--color-text-tertiary)] text-sm">Loading...</div>
  </div>
);

function App() {
  const { user, loading, signOut, betaDenied, setBetaDenied } = useAuth();
  const { baseColor, paletteIdx } = useUIStore();
  const [signInMessage, setSignInMessage] = useState("");
  const [signInError, setSignInError] = useState("");
  const [isSignInModalOpen, setSignInModalOpen] = useState(false);
  // An invite link (?invite=CODE) opens the sign-up form with the code filled in.
  const [inviteFromUrl] = useState(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get("invite");
      return raw ? normalizeInviteCode(raw) : "";
    } catch {
      return "";
    }
  });
  const [isSignUpModalOpen, setSignUpModalOpen] = useState(() => inviteFromUrl !== "");
  const [isGuestMode, setIsGuestMode] = useState(() => guestDashboardService.isGuestMode());

  // Apply adaptive theme + accent palette on mount and when they change
  useEffect(() => {
    const palette = ACCENT_PALETTES[paletteIdx] ?? "sky";
    document.documentElement.setAttribute("data-palette", palette);
    applyTheme(baseColor);
  }, [baseColor, paletteIdx]);

  // Migrate guest data to Firestore when user authenticates. The outcome used
  // to go only to the console; now the user is told what moved.
  // Signing in ends guest mode, so signing out later lands on the splash page.
  // With nothing to migrate that happens at once, while rendering.
  // Starts unset so a user present on the first render counts as a sign-in.
  const [signedInUid, setSignedInUid] = useState<string | undefined>(undefined);
  if (user?.uid !== signedInUid) {
    setSignedInUid(user?.uid);
    if (user && !guestMigrationService.hasGuestData()) setIsGuestMode(false);
  }

  useEffect(() => {
    if (!user || !guestMigrationService.hasGuestData()) return;
    guestMigrationService
      .migrateToUser(user.uid)
      .then((summary) => {
        setIsGuestMode(false);
        if (summary) setSignInMessage(describeMigration(summary));
      })
      .catch((err) => {
        console.error("Guest data migration failed:", err);
        setSignInError(
          "Couldn't move your guest links to this account. They're still saved in this browser — reload to try again."
        );
      });
  }, [user]);

  // Both notices are transient.
  useEffect(() => {
    if (!signInMessage && !signInError) return;
    const timer = setTimeout(() => {
      setSignInMessage("");
      setSignInError("");
    }, 10000);
    return () => clearTimeout(timer);
  }, [signInMessage, signInError]);

  const handleEnterGuestMode = () => {
    guestDashboardService.enterGuestMode();
    setIsGuestMode(true);
  };

  const openSignUp = () => {
    setSignInModalOpen(false);
    setBetaDenied(false);
    setSignUpModalOpen(true);
  };

  // Add event listener for 'open-sign-in-modal' custom event dispatched from SignUpModal
  useEffect(() => {
    const handleOpenSignInModal = () => {
      setSignUpModalOpen(false);
      setSignInModalOpen(true);
    };

    window.addEventListener("open-sign-in-modal", handleOpenSignInModal);

    return () => {
      window.removeEventListener("open-sign-in-modal", handleOpenSignInModal);
    };
  }, []);

  // Handle sign up. Redeems the invite code and creates the account record in
  // one transaction. The modal awaits this and shows its own confirmation or
  // inline error (including which way the code was refused).
  const handleSignUp = async (data: {
    email: string;
    firstName: string;
    lastName: string;
    zip: string;
    ip?: string;
    inviteCode: string;
  }) => {
    await inviteService.redeem(data);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] transition-colors">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/brand" element={<BrandGuide />} />
          <Route path="/email-signin" element={<EmailSignIn />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route
            path="*"
            element={
              loading ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-[var(--color-text-tertiary)] text-sm">Loading...</div>
                </div>
              ) : !user && isGuestMode ? (
                <>
                  <GuestApp
                    onSignInClick={() => setSignInModalOpen(true)}
                    onSignUpClick={() => setSignUpModalOpen(true)}
                  />
                  <SignInModal
                    isOpen={isSignInModalOpen}
                    onClose={() => setSignInModalOpen(false)}
                    onRequestInvite={openSignUp}
                  />
                  <SignUpModal
                    isOpen={isSignUpModalOpen}
                    onClose={() => setSignUpModalOpen(false)}
                    onSubmit={handleSignUp}
                    initialInviteCode={inviteFromUrl}
                  />
                </>
              ) : !user ? (
                <>
                  <SplashPage
                    onSignInClick={() => setSignInModalOpen(true)}
                    onGetStartedClick={() => setSignUpModalOpen(true)}
                    onTryAsGuestClick={handleEnterGuestMode}
                  />
                  {betaDenied && (
                    // This used to be an undismissable overlay that sat on top of
                    // every splash button, so the only way out was a reload.
                    <div
                      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                      onClick={() => setBetaDenied(false)}
                    >
                      <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="beta-denied-title"
                        className="bg-[var(--color-bg-secondary)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-6 max-w-sm w-full text-center shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3
                          id="beta-denied-title"
                          className="font-heading text-lg font-semibold text-[var(--color-text-primary)] mb-2"
                        >
                          No account for that email yet
                        </h3>
                        <p className="text-sm text-[var(--color-text-tertiary)] mb-5">
                          Sign-in links only work for emails that have an account. If you have an
                          invite code, create an account with this email and we'll send a fresh
                          link. Otherwise try Start as a guest in the meantime.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                          <button type="button" className="btn btn-primary" onClick={openSignUp}>
                            Create an account
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setBetaDenied(false);
                              handleEnterGuestMode();
                            }}
                          >
                            Try as guest
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setBetaDenied(false)}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <SignInModal
                    isOpen={isSignInModalOpen}
                    onClose={() => setSignInModalOpen(false)}
                    onRequestInvite={openSignUp}
                  />
                  <SignUpModal
                    isOpen={isSignUpModalOpen}
                    onClose={() => setSignUpModalOpen(false)}
                    onSubmit={handleSignUp}
                    initialInviteCode={inviteFromUrl}
                    onTryAsGuest={() => {
                      setSignUpModalOpen(false);
                      handleEnterGuestMode();
                    }}
                  />
                </>
              ) : (
                <AuthenticatedApp
                  user={user}
                  signOut={signOut}
                  isSignInModalOpen={isSignInModalOpen}
                  setSignInModalOpen={setSignInModalOpen}
                  isSignUpModalOpen={isSignUpModalOpen}
                  setSignUpModalOpen={setSignUpModalOpen}
                  signInMessage={signInMessage}
                  signInError={signInError}
                  onSignUp={handleSignUp}
                />
              )
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
